import { AccountKindService } from '@frontendlib/common/services/account-kind.service';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  type WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Constants } from '@frontendlib/common/types/Constants';
import { CurrentUserStore } from '@frontendlib/common/services/current-user.store';
import { DateFormatService } from '@modules/common/services/date-format.service';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { Note } from '@modules/notes/types/note';
import { NotesFilters } from '@modules/notes/types/notes-filters';
import { forkJoin, take, type Subscription } from 'rxjs';
import { auditTime } from 'rxjs/operators';
import { NoteAssignmentFeedService } from './note-assignment-feed.service';
import { NoteCollaborationApiService } from './note-collaboration-api.service';

interface DatedNote {
  readonly note: Note;
  readonly heading: string | null;
}

interface AssignedScope {
  readonly tenantId: string;
  readonly filters: NotesFilters;
  readonly key: string;
}

interface StatusProjection {
  readonly status: 'open' | 'resolved';
  readonly page: WritableSignal<number>;
  readonly items: WritableSignal<readonly Note[]>;
  readonly count: WritableSignal<number>;
  readonly unreadCount: WritableSignal<number>;
  readonly loading: WritableSignal<boolean>;
  readonly error: WritableSignal<boolean>;
}

let nextAssignedListId = 0;

@Component({
  selector: 'app-note-assigned-list',
  templateUrl: './note-assigned-list.component.html',
  styleUrl: './note-assigned-list.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class NoteAssignedListComponent {
  private readonly instanceId = ++nextAssignedListId;
  protected readonly openPanelId = `assigned-open-section-${this.instanceId}`;
  protected readonly resolvedPanelId = `assigned-resolved-section-${this.instanceId}`;
  protected readonly pageSize = Constants.PAGE_SIZE;
  public readonly filters = input<NotesFilters | null>(null);
  public readonly openNote = output<Note>();
  public readonly doubleClickNote = output<Note>();
  private readonly api = inject(NoteCollaborationApiService);
  private readonly feed = inject(NoteAssignmentFeedService, { optional: true });
  private readonly spaceContext = inject(SpaceContextStore);
  private readonly accountKind = inject(AccountKindService);
  private readonly currentUser = inject(CurrentUserStore);
  private readonly dateFormat = inject(DateFormatService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly revision = signal(0);
  private scopeKey: string | null | undefined;
  private pinnedLoadedPages = 1;
  private pinnedMoreRequest: Subscription | null = null;
  protected readonly pinned = signal<readonly Note[]>([]);
  protected readonly open = signal<readonly Note[]>([]);
  protected readonly resolved = signal<readonly Note[]>([]);
  protected readonly pinnedCount = signal(0);
  protected readonly openCount = signal(0);
  protected readonly resolvedCount = signal(0);
  protected readonly openUnreadCount = signal(0);
  protected readonly resolvedUnreadCount = signal(0);
  protected readonly selectedStatus = signal<'open' | 'resolved'>('open');
  protected readonly openPage = signal(1);
  protected readonly resolvedPage = signal(1);
  protected readonly pinnedLoading = signal(false);
  protected readonly pinnedLoadingMore = signal(false);
  protected readonly openLoading = signal(false);
  protected readonly resolvedLoading = signal(false);
  protected readonly pinnedError = signal(false);
  protected readonly openError = signal(false);
  protected readonly resolvedError = signal(false);
  protected readonly accessDenied = signal(false);
  protected readonly activePage = computed(() =>
    this.selectedStatus() === 'open' ? this.openPage() : this.resolvedPage(),
  );
  protected readonly activeCount = computed(() =>
    this.selectedStatus() === 'open' ? this.openCount() : this.resolvedCount(),
  );
  protected readonly activeLoading = computed(() =>
    this.selectedStatus() === 'open' ? this.openLoading() : this.resolvedLoading(),
  );
  protected readonly anyLoading = computed(() => this.pinnedLoading() || this.openLoading() || this.resolvedLoading());
  protected readonly anyError = computed(() => this.pinnedError() || this.openError() || this.resolvedError());
  protected readonly datedOpen = computed<readonly DatedNote[]>(() => this.datedNotes(this.open()));
  protected readonly datedResolved = computed<readonly DatedNote[]>(() => this.datedNotes(this.resolved()));
  private readonly scope = computed<AssignedScope | null>(
    () => {
      const actorId = this.currentUser.accountId();
      const tenantId = this.accountKind.isTeam() && this.spaceContext.insideTeam() ? this.spaceContext.contextId() : null;
      const filters = this.filters();
      if (!actorId || !tenantId || !filters) return null;
      const params = Object.entries(filters.format())
        .filter(([name]) => name !== 'postponed_on_top_till')
        .sort(([left], [right]) => left.localeCompare(right));
      return { tenantId, filters, key: JSON.stringify([actorId, tenantId, params]) };
    },
    { equal: (previous, current) => previous?.key === current?.key },
  );

  public constructor() {
    effect(() => {
      const key = this.scope()?.key ?? null;
      if (key === this.scopeKey) return;
      this.scopeKey = key;
      this.accessDenied.set(false);
      this.openPage.set(1);
      this.resolvedPage.set(1);
      this.pinnedLoadedPages = 1;
    });
    this.watchPinned();
    this.watchStatus({
      status: 'open',
      page: this.openPage,
      items: this.open,
      count: this.openCount,
      unreadCount: this.openUnreadCount,
      loading: this.openLoading,
      error: this.openError,
    });
    this.watchStatus({
      status: 'resolved',
      page: this.resolvedPage,
      items: this.resolved,
      count: this.resolvedCount,
      unreadCount: this.resolvedUnreadCount,
      loading: this.resolvedLoading,
      error: this.resolvedError,
    });
    this.feed?.invalidated$.pipe(auditTime(80), takeUntilDestroyed()).subscribe(() => this.reload());
  }

  private datedNotes(notes: readonly Note[]): readonly DatedNote[] {
    let previousDate = '';
    return notes.map((note) => {
      const date = this.dateFormat.format(note.createdAt, { format: 'date' });
      const heading = date === previousDate ? null : this.dateFormat.format(note.createdAt, { format: 'relativeDate' });
      previousDate = date;
      return { note, heading };
    });
  }

  private assignedNote(
    item: Partial<Note>,
    page: { readonly pendingUids?: readonly string[]; readonly reviewUids?: readonly string[] },
  ): Note {
    const note = new Note(item);
    note.pendingAssignment = (page.pendingUids ?? []).includes(note.id);
    note.pendingReview = (page.reviewUids ?? []).includes(note.id);
    return note;
  }
  private watchPinned(): void {
    let previousKey: string | null | undefined;
    effect((onCleanup) => {
      const scope = this.scope();
      this.revision();
      const denied = this.accessDenied();
      this.pinnedMoreRequest?.unsubscribe();
      this.pinnedMoreRequest = null;
      this.pinnedLoadingMore.set(false);
      if (scope?.key !== previousKey) {
        previousKey = scope?.key ?? null;
        this.pinned.set([]);
        this.pinnedCount.set(0);
        this.pinnedError.set(false);
      }
      if (!scope || denied) {
        this.pinnedLoading.set(false);
        return;
      }
      this.pinnedLoading.set(true);
      const requests = Array.from({ length: this.pinnedLoadedPages }, (_, page) =>
        this.api.assigned(
          scope.tenantId,
          undefined,
          page * this.pageSize,
          this.pageSize,
          this.withPinned(scope.filters, true),
        ),
      );
      const request = forkJoin(requests).subscribe({
        next: (pages) =>
          untracked(() => {
            if (scope !== this.scope() || this.accessDenied()) return;
            this.pinned.set(
              pages.flatMap((page) => page.items.map((item) => this.assignedNote(item, page))),
            );
            this.pinnedCount.set(pages[0].count);
            this.pinnedLoading.set(false);
            this.pinnedError.set(false);
          }),
        error: (error: unknown) =>
          untracked(() => {
            if (scope !== this.scope()) return;
            this.pinnedLoading.set(false);
            if (this.isForbidden(error)) this.denyAccess();
            else this.pinnedError.set(true);
          }),
      });
      onCleanup(() => request.unsubscribe());
    });
  }

  private watchStatus({ status, page, items, count, unreadCount, loading, error }: StatusProjection): void {
    let previousKey: string | null | undefined;
    let previousPage = 1;
    effect((onCleanup) => {
      const scope = this.scope();
      const currentPage = page();
      this.revision();
      const denied = this.accessDenied();
      if (scope?.key !== previousKey) {
        previousKey = scope?.key ?? null;
        items.set([]);
        count.set(0);
        unreadCount.set(0);
        error.set(false);
        if (currentPage !== 1) {
          page.set(1);
          return;
        }
      } else if (currentPage !== previousPage) {
        items.set([]);
        error.set(false);
      }
      previousPage = currentPage;
      if (!scope || denied) {
        loading.set(false);
        return;
      }
      loading.set(true);
      const request = this.api
        .assigned(
          scope.tenantId,
          status,
          (currentPage - 1) * this.pageSize,
          this.pageSize,
          this.withPinned(scope.filters, false),
        )
        .subscribe({
          next: (result) =>
            untracked(() => {
              if (scope !== this.scope() || currentPage !== page() || this.accessDenied()) return;
              count.set(result.count);
              unreadCount.set(result.unreadCount);
              const lastPage = Math.max(1, Math.ceil(result.count / this.pageSize));
              if (currentPage > lastPage) {
                page.set(lastPage);
                return;
              }
              items.set(result.items.map((item) => this.assignedNote(item, result)));
              loading.set(false);
              error.set(false);
            }),
          error: (requestError: unknown) =>
            untracked(() => {
              if (scope !== this.scope() || currentPage !== page()) return;
              loading.set(false);
              if (this.isForbidden(requestError)) this.denyAccess();
              else error.set(true);
            }),
        });
      onCleanup(() => request.unsubscribe());
    });
  }

  private isForbidden(error: unknown): boolean {
    return error instanceof HttpErrorResponse && error.status === 403;
  }

  private denyAccess(): void {
    this.accessDenied.set(true);
    this.pinned.set([]);
    this.open.set([]);
    this.resolved.set([]);
    this.pinnedCount.set(0);
    this.openCount.set(0);
    this.resolvedCount.set(0);
    this.openUnreadCount.set(0);
    this.resolvedUnreadCount.set(0);
  }

  protected reload(): void {
    this.revision.update((revision) => revision + 1);
  }

  protected changePage(status: 'open' | 'resolved', nextPage: number): void {
    if (status === 'open') this.openPage.set(nextPage);
    else this.resolvedPage.set(nextPage);
  }

  protected changeActivePage(nextPage: number): void {
    this.changePage(this.selectedStatus(), nextPage);
  }

  protected loadMorePinned(): void {
    const scope = this.scope();
    if (!scope || this.pinnedLoading() || this.pinnedLoadingMore() || this.pinned().length >= this.pinnedCount())
      return;
    const offset = this.pinned().length;
    const revision = this.revision();
    this.pinnedLoadingMore.set(true);
    this.pinnedMoreRequest = this.api
      .assigned(scope.tenantId, undefined, offset, this.pageSize, this.withPinned(scope.filters, true))
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.pinnedLoadingMore.set(false);
          if (scope !== this.scope() || revision !== this.revision() || this.accessDenied()) return;
          this.pinned.update((current) => [
            ...current,
            ...result.items
              .filter((item) => !current.some((seen) => seen.id === item.id))
              .map((item) => this.assignedNote(item, result)),
          ]);
          this.pinnedCount.set(result.count);
          this.pinnedLoadedPages = Math.max(1, Math.ceil(this.pinned().length / this.pageSize));
        },
        error: (error: unknown) => {
          this.pinnedLoadingMore.set(false);
          if (scope !== this.scope()) return;
          if (this.isForbidden(error)) this.denyAccess();
          else this.pinnedError.set(true);
        },
      });
  }

  private withPinned(filters: NotesFilters, pinned: boolean): NotesFilters {
    return new NotesFilters({ ...filters, pinned });
  }
}
